import React, { useState } from "react";
import MainLayout from "../../components/MainLayout/MainLayout";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faBriefcase,
  faGraduationCap,
  faLaptopCode,
  faChevronDown,
  faChevronUp,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { useInView } from "react-intersection-observer";
import "./CV.css";

const CV = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
      },
    },
  };

  // Handle redirect to CV download
  const handleRedirect = () => {
    window.location.href =
      "https://1drv.ms/b/s!Akx4y8HSgYVPirBY8QPkTQlOosdnNw?e=0XhLty";
  };

  // Accordion state
  const [expanded, setExpanded] = useState({
    education: true,
    software: false,
    work: false,
    projects: false,
  });

  // Toggle accordion sections
  const toggleAccordion = (section) => {
    setExpanded((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Intersection observer hooks
  const [headerRef, headerInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [educationRef, educationInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [workRef, workInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [skillsRef, skillsInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Skills data
  const skills = {
    technical: [
      "JavaScript (ES6+)",
      "React.js",
      "Node.js",
      "C#",
      "ASP.NET",
      "SQL",
      "MongoDB",
      "HTML5/CSS3",
      "Bootstrap",
      "TailwindCSS",
      "TypeScript",
    ],
    frameworks: [
      "MERN Stack",
      "Express.js",
      ".NET Core",
      "MVC Pattern",
      "RESTful APIs",
      "Next.js",
      "Blazor",
      "React Native",
    ],
    tools: [
      "Git/GitHub",
      "VS Code",
      "Visual Studio",
      "Postman",
      "Docker",
      "Azure",
      "MongoDB Atlas",
      "Figma",
    ],
    softSkills: [
      "Problem Solving",
      "Communication",
      "Team Collaboration",
      "Time Management",
      "Adaptability",
      "Critical Thinking",
      "Attention to Detail",
    ],
  };

  // Work experience data
  const workExperience = [
    {
      company: "Appointify",
      role: "Full Stack Developer",
      period: "Jan 2023 – Jan 2024",
      description: [
        "Developed secure user authentication, appointment management, and role-based access control using C# and ASP.NET MVC.",
        "Implemented SQL databases for CRUD functionality.",
        "Automated SMS notifications for real-time updates.",
        "Conducted testing to ensure application reliability.",
      ],
    },
    {
      company: "Sahii Blog App",
      role: "MERN Stack Developer",
      period: "Jan 2023 – Jan 2024",
      description: [
        "Built a full-stack MERN blog app using React, Node.js, Express, and MongoDB.",
        "Implemented JWT for authentication, tested APIs with Postman.",
        "Designed a responsive UI with Tailwind CSS and Material UI.",
      ],
    },
    {
      company: "Convo-GPT",
      role: "Frontend Developer",
      period: "Dec 2023 – Jan 2024",
      description: [
        "Developed an AI SaaS Chat Bot using the MERN stack, similar to ChatGPT.",
        "Implemented JWT authentication and used MongoDB for chat storage.",
        "Created a modern, responsive UI using Vite and TypeScript.",
      ],
    },
    {
      company: "Royal Mail",
      role: "Mail Processor",
      period: "Mar 2017 – Oct 2024",
      description: [
        "7 years as a Mail Processor, handling mail operations with attention to detail.",
        "Worked effectively under pressure, meeting daily targets.",
        "Led training for new employees during peak seasons.",
      ],
    },
  ];

  // Education data
  const education = [
    {
      institution: "The Manchester Metropolitan University",
      degree: "BSc Software Development - Upper Second-Class Honours",
      period: "2020 – 2023",
      details: [
        "Modules: Advanced Programming, Web Server & Client, Data Structures & Algorithms, Database Driven Websites, Object-Oriented Programming, UI Design, Research Project",
        "Projects: Sleek Racer (C++), Veterinary Surgery App (Java), Crick Time web app (HTML, CSS, PHP, JavaScript, MySQL)",
      ],
    },
    {
      institution: "Future Talent Full Stack Software Engineer Stream",
      degree: "Software Engineering Program",
      period: "2024",
      details: [
        "12-week intensive program covering SDLC, Java, SQL, HTML/CSS/JavaScript (React/Angular), C#, ASP.NET, MAUI and Blazor",
      ],
    },
  ];

  return (
    <MainLayout>
      <motion.div
        className="cv-wrapper"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* CV Header section */}
        <section className="cv-header-section" ref={headerRef}>
          <div className="cv-container">
            <motion.div
              className="cv-header"
              initial={{ opacity: 0, y: 30 }}
              animate={
                headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }
              }
              transition={{ duration: 0.8 }}
            >
              <h1 className="cv-title">Curriculum Vitae</h1>
              <div className="cv-title-underline"></div>

              <motion.div
                className="cv-intro"
                initial={{ opacity: 0 }}
                animate={headerInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <p>
                  Welcome to my professional journey! This document showcases my
                  experience, skills, and achievements. Feel free to explore or
                  download the complete CV.
                </p>

                <motion.button
                  className="cv-download-btn"
                  onClick={handleRedirect}
                  whileHover={{
                    y: -5,
                    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <FontAwesomeIcon icon={faDownload} /> Download Full CV
                </motion.button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* CV Content sections */}
        <section className="cv-content-section">
          <div className="cv-container">
            <div className="cv-grid">
              {/* Left column with experience and education */}
              <div className="cv-left-column">
                {/* Education section */}
                <div className="cv-section" ref={educationRef}>
                  <div
                    className={`cv-section-header ${
                      expanded.education ? "expanded" : ""
                    }`}
                    onClick={() => toggleAccordion("education")}
                  >
                    <div className="cv-section-title">
                      <FontAwesomeIcon
                        icon={faGraduationCap}
                        className="cv-section-icon"
                      />
                      <h2>Education</h2>
                    </div>
                    <FontAwesomeIcon
                      icon={expanded.education ? faChevronUp : faChevronDown}
                    />
                  </div>

                  {expanded.education && (
                    <motion.div
                      className="cv-section-content"
                      variants={containerVariants}
                      initial="hidden"
                      animate={educationInView ? "visible" : "hidden"}
                    >
                      {education.map((item, index) => (
                        <motion.div
                          className="cv-timeline-item"
                          key={index}
                          variants={itemVariants}
                        >
                          <div className="cv-timeline-marker"></div>
                          <div className="cv-timeline-content">
                            <div className="cv-timeline-header">
                              <h3>{item.institution}</h3>
                              <span className="cv-period">{item.period}</span>
                            </div>
                            <p className="cv-degree">{item.degree}</p>
                            <ul className="cv-details-list">
                              {item.details.map((detail, idx) => (
                                <li key={idx}>{detail}</li>
                              ))}
                            </ul>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </div>

                {/* Work Experience section */}
                <div className="cv-section" ref={workRef}>
                  <div
                    className={`cv-section-header ${
                      expanded.work ? "expanded" : ""
                    }`}
                    onClick={() => toggleAccordion("work")}
                  >
                    <div className="cv-section-title">
                      <FontAwesomeIcon
                        icon={faBriefcase}
                        className="cv-section-icon"
                      />
                      <h2>Work Experience</h2>
                    </div>
                    <FontAwesomeIcon
                      icon={expanded.work ? faChevronUp : faChevronDown}
                    />
                  </div>

                  {expanded.work && (
                    <motion.div
                      className="cv-section-content"
                      variants={containerVariants}
                      initial="hidden"
                      animate={workInView ? "visible" : "hidden"}
                    >
                      {workExperience.map((job, index) => (
                        <motion.div
                          className="cv-timeline-item"
                          key={index}
                          variants={itemVariants}
                        >
                          <div className="cv-timeline-marker"></div>
                          <div className="cv-timeline-content">
                            <div className="cv-timeline-header">
                              <h3>{job.company}</h3>
                              <span className="cv-period">{job.period}</span>
                            </div>
                            <p className="cv-role">{job.role}</p>
                            <ul className="cv-details-list">
                              {job.description.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Right column with skills */}
              <div className="cv-right-column" ref={skillsRef}>
                <motion.div
                  className="cv-skills-section"
                  initial={{ opacity: 0, x: 30 }}
                  animate={
                    skillsInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }
                  }
                  transition={{ duration: 0.8 }}
                >
                  <div className="cv-skills-header">
                    <FontAwesomeIcon
                      icon={faLaptopCode}
                      className="cv-section-icon"
                    />
                    <h2>Professional Skills</h2>
                  </div>

                  <div className="cv-skills-content">
                    <div className="cv-skill-category">
                      <h3>Technical Skills</h3>
                      <div className="cv-skill-tags">
                        {skills.technical.map((skill, index) => (
                          <motion.span
                            className="cv-skill-tag"
                            key={index}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={
                              skillsInView
                                ? { opacity: 1, scale: 1 }
                                : { opacity: 0, scale: 0.8 }
                            }
                            transition={{ duration: 0.4, delay: index * 0.05 }}
                          >
                            {skill}
                          </motion.span>
                        ))}
                      </div>
                    </div>

                    <div className="cv-skill-category">
                      <h3>Frameworks & Libraries</h3>
                      <div className="cv-skill-tags">
                        {skills.frameworks.map((skill, index) => (
                          <motion.span
                            className="cv-skill-tag"
                            key={index}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={
                              skillsInView
                                ? { opacity: 1, scale: 1 }
                                : { opacity: 0, scale: 0.8 }
                            }
                            transition={{ duration: 0.4, delay: index * 0.05 }}
                          >
                            {skill}
                          </motion.span>
                        ))}
                      </div>
                    </div>

                    <div className="cv-skill-category">
                      <h3>Tools & Platforms</h3>
                      <div className="cv-skill-tags">
                        {skills.tools.map((skill, index) => (
                          <motion.span
                            className="cv-skill-tag"
                            key={index}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={
                              skillsInView
                                ? { opacity: 1, scale: 1 }
                                : { opacity: 0, scale: 0.8 }
                            }
                            transition={{ duration: 0.4, delay: index * 0.05 }}
                          >
                            {skill}
                          </motion.span>
                        ))}
                      </div>
                    </div>

                    <div className="cv-skill-category">
                      <h3>Soft Skills</h3>
                      <div className="cv-skill-tags">
                        {skills.softSkills.map((skill, index) => (
                          <motion.span
                            className="cv-skill-tag soft-skill"
                            key={index}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={
                              skillsInView
                                ? { opacity: 1, scale: 1 }
                                : { opacity: 0, scale: 0.8 }
                            }
                            transition={{ duration: 0.4, delay: index * 0.05 }}
                          >
                            {skill}
                          </motion.span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cv-summary-section"
                  initial={{ opacity: 0, y: 30 }}
                  animate={
                    skillsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }
                  }
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  <h3>Professional Summary</h3>
                  <p>
                    Motivated Software Engineer with a BSc in Software
                    Development (Hons) with a 2:1 from Manchester Metropolitan
                    University. I am excited to utilise my hands-on skills and
                    knowledge to diverse industry projects and develop my skills
                    further as a Software Engineer. I aspire to develop software
                    solutions that solve real-world problems and provide
                    terrific service to clients.
                  </p>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cv-cta-section">
          <div className="cv-container">
            <motion.div
              className="cv-cta-content"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2>Ready to Connect?</h2>
              <p>
                Interested in my background and experience? Feel free to reach
                out or check out my projects!
              </p>
              <div className="cv-cta-buttons">
                <motion.a
                  href="/Portfolio"
                  className="cv-primary-btn"
                  whileHover={{
                    y: -5,
                    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                  }}
                  transition={{ duration: 0.2 }}
                >
                  View My Projects <FontAwesomeIcon icon={faArrowRight} />
                </motion.a>
                <motion.button
                  onClick={handleRedirect}
                  className="cv-secondary-btn"
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  Download Full CV <FontAwesomeIcon icon={faDownload} />
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </MainLayout>
  );
};

export default CV;
